import { useCallback, useContext, useEffect, useReducer, useRef } from 'react'
import moment from 'moment'

import { LanguageContext } from '../../context/LanguageContext'
import { util } from '../../util'

export const useFilter = (initialState, toggleHandler, appliedFilters, dispatchFilters) => {
    const { strings } = useContext(LanguageContext)

    const getDefaultDateValues = date => (date ? moment(date).format('MM/DD/YYYY') : null)

    const initializeStateFromPayload = (state, payload) => {
        const updatedState = { ...state }
        updatedState['filterState'] = payload
        payload.forEach(filter => {
            if (!filter.isNotFilterSideMenu) {
                const cloneFilter = { ...filter }
                const key = cloneFilter.key
                const { list = [] } = updatedState[key] || {}
                if (cloneFilter.type === 'range') {
                    /** Copying the date range values */
                    updatedState[key]['list'] = list.length
                        ? list
                        : [
                              getDefaultDateValues(cloneFilter.start_value),
                              getDefaultDateValues(cloneFilter.end_value),
                          ]
                } else if (updatedState.hasOwnProperty(cloneFilter.key)) {
                    /** copying from redux filter state to the component filter state */
                    if (cloneFilter.type === 'string') {
                        updatedState[key]['value'] = cloneFilter.display_value
                    } else {
                        updatedState[key]['list'] = list.length ? list : cloneFilter.display_value
                    }
                    updatedState[key]['type'] = cloneFilter.type
                    if (cloneFilter.baseFilterList && cloneFilter.baseFilterList.length) {
                        updatedState[key]['baseFilterList'] = cloneFilter.baseFilterList
                    }
                } else if (!!cloneFilter.initialize) {
                    /** this else block will create the dynamic state values instead of pre-defined state values */
                    /** please check the appropriate filter data model to find the initialize method */
                    updatedState[cloneFilter.key] = cloneFilter.initialize()
                }
            }
        })

        return updatedState
    }

    const stateTypes = {
        INPUT_CHANGE: 'INPUT_CHANGE',
        UPDATE_FILTER_LIST: 'UPDATE_FILTER_LIST',
        CLEAR_INPUT_FIELD: 'CLEAR_INPUT_FIELD',
        FILTER_LIST: 'FILTER_LIST',
        ADD_CHECKBOX: 'ADD_CHECKBOX',
        REMOVE_CHECKBOX: 'REMOVE_CHECKBOX',
        ADD_DATE_RANGE: 'ADD_DATE_RANGE',
        REMOVE_DATE_RANGE: 'REMOVE_DATE_RANGE',
        SET_FILTER_STATE: 'SET_FILTER_STATE',
        CLEAR_ALL_FIELDS: 'CLEAR_ALL_FIELDS',
        CLEAR_STATE_FIELDS: 'CLEAR_STATE_FIELDS',
        DATE_CHANGE: 'DATE_CHANGE',
        UPDATE_AUDIENCE_LIST: 'UPDATE_AUDIENCE_LIST',
        UPDATE_STATE_FIELD: 'UPDATE_STATE_FIELD',
        REPLACE_LIST_FIELD: 'REPLACE_LIST_FIELD',
    }

    const reducer = (state, action) => {
        const { type, field, payload } = action
        const {
            clearStateFields,
            clearAllStateFields,
            shallowHelper,
            deepShallowHelper,
            deepShallowArrayHelper,
            deepShallowFilterHelper,
        } = util
        switch (type) {
            case stateTypes.ADD_CHECKBOX:
                return deepShallowArrayHelper(state, 'list', field, payload)

            case stateTypes.CLEAR_ALL_FIELDS:
                //Clearing all state fields
                return clearAllStateFields(state)

            case stateTypes.CLEAR_INPUT_FIELD:
                return deepShallowHelper(state, 'value', field, '')

            case stateTypes.CLEAR_STATE_FIELDS:
                //Clear State fields but not the filterState
                return clearStateFields(state)

            case stateTypes.DATE_CHANGE:
                return deepShallowHelper(state, 'list', field, payload)

            case stateTypes.FILTER_LIST:
                return deepShallowFilterHelper(state, 'list', field, payload)

            case stateTypes.INPUT_CHANGE:
                return deepShallowHelper(state, 'value', field, payload)

            case stateTypes.REMOVE_CHECKBOX:
                return deepShallowFilterHelper(state, 'list', field, payload)

            case stateTypes.SET_FILTER_STATE:
                return initializeStateFromPayload(state, payload)

            case stateTypes.UPDATE_AUDIENCE_LIST:
                return deepShallowHelper(state, 'list', field, payload)

            case stateTypes.UPDATE_FILTER_LIST:
                return deepShallowArrayHelper(state, 'list', field, payload)

            case stateTypes.UPDATE_STATE_FIELD:
                return shallowHelper(state, { [field]: payload })

            case stateTypes.REPLACE_LIST_FIELD:
                return deepShallowHelper(state, 'list', field, payload)

            default:
                return state
        }
    }

    const applyBtnRef = useRef({})
    const [state, dispatch] = useReducer(reducer, initialState)
    const filterMenuRef = useRef({})

    useEffect(() => {
        dispatch({
            type: stateTypes.SET_FILTER_STATE,
            payload: appliedFilters,
        })
        enableClearAllButton(appliedFilters)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters])

    const inputChangeHandler = useCallback(e => {
        dispatch({
            type: stateTypes.INPUT_CHANGE,
            field: e.target.name,
            payload: e.target.value,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSelectInputChange = useCallback((value, name) => {
        dispatch({
            type: stateTypes.INPUT_CHANGE,
            field: name,
            payload: value,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const updateFilterList = useCallback((value, name) => {
        dispatch({
            type: stateTypes.UPDATE_FILTER_LIST,
            field: name,
            payload: value,
        })

        dispatch({
            type: stateTypes.CLEAR_INPUT_FIELD,
            field: name,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const removeFilterList = useCallback((value, name) => {
        dispatch({
            type: stateTypes.FILTER_LIST,
            field: name,
            payload: value,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const checkboxHandler = useCallback(e => {
        if (e.target.checked) {
            dispatch({
                type: stateTypes.ADD_CHECKBOX,
                field: e.target.name,
                payload: e.target.id,
            })
        } else {
            dispatch({
                type: stateTypes.REMOVE_CHECKBOX,
                field: e.target.name,
                payload: e.target.id,
            })
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const dateChangeHandler = useCallback(
        (value, name) => {
            const dateRangeField = !!state.updated_at ? 'updated_at' : 'date_range'
            let dateRange = state[dateRangeField].list
            let error = '',
                field = ''
            if (name === 'startDate') {
                dateRange[0] = value
                field = 'startDateError'
                if (dateRange[0] !== null && !moment(dateRange[0]).isValid()) {
                    error = strings.DATE_FORMAT_ERROR
                } else if (dateRange[1] && moment(value).isAfter(moment(dateRange[1]))) {
                    // If both start and end dates are selected and if start date is after end date, then set end date same as start date
                    dateRange[1] = value
                }
            } else {
                field = 'endDateError'
                dateRange[1] = value
                if (dateRange[1] !== null && !moment(dateRange[1]).isValid()) {
                    error = strings.DATE_FORMAT_ERROR
                }
            }
            if (dateRange.every(date => date === null)) {
                dateRange = []
            }
            updateStateFields(field, error)
            dispatch({
                type: stateTypes.DATE_CHANGE,
                field: dateRangeField,
                payload: dateRange,
            })
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state.date_range]
    )

    const clearAllHandler = useCallback(() => {
        applyBtnRef.current.isEnable = false
        dispatch({
            type: stateTypes.CLEAR_ALL_FIELDS,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const closeSideMenuHandler = useCallback(() => {
        dispatch({
            type: stateTypes.SET_FILTER_STATE,
            payload: appliedFilters,
        })
        toggleHandler()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toggleHandler])

    const getDateRangeObject = (list, appliedFilterState) => {
        appliedFilterState['start_value'] = list[0]
            ? util.getStartOfDay(moment(list[0]).format('MM/DD/YYYY')).toISOString()
            : ''
        appliedFilterState['end_value'] = list[1]
            ? util.getEndOfDay(moment(list[1]).format('MM/DD/YYYY')).toISOString()
            : ''
        appliedFilterState['display_value'] = util.getDateRangeDisplayPill(list[0], list[1])
        return appliedFilterState
    }

    /** The following function will take the data from component state and create the redux
     * filter state structure and updates the filter */
    const filterApplyHandler = () => {
        /** loop through redux filter state list */
        const updatedFilterList = state.filterState.reduce((acc, filter) => {
            const {
                key,
                postUtil = undefined,
                displayUtil = undefined,
                isNotFilterSideMenu = false,
            } = filter

            if (isNotFilterSideMenu) {
                acc.push(filter)
                return acc
            }
            let updatedFilter = { ...filter }
            if (state.hasOwnProperty(key)) {
                /** Filter Component level state */
                const { list, ...rest } = state[key]
                if (key === 'date_range') {
                    updatedFilter = getDateRangeObject(list, updatedFilter)
                } else {
                    const config = {
                        list,
                        key,
                        state,
                        ...rest,
                    }
                    /** please check the appropriate filter data model to find postUtil and displayUtil methods */
                    updatedFilter['post_value'] = !!postUtil ? filter.postUtil(config) : list
                    updatedFilter['display_value'] = !!displayUtil
                        ? filter.displayUtil(config)
                        : list
                    updatedFilter['display_name'] = !!filter.displayNameUtil
                        ? filter.displayNameUtil(config)
                        : filter.display_name
                }
            }

            acc.push(updatedFilter)
            return acc
        }, [])
        /** updating the redux filter state and closing the filter side menu */
        // console.log('updatedFilterList', updatedFilterList)

        dispatchFilters(updatedFilterList)
        !!toggleHandler && toggleHandler()
    }

    const audienceChangeHandler = useCallback((name, value) => {
        dispatch({
            type: stateTypes.UPDATE_AUDIENCE_LIST,
            field: name,
            payload: value,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const updateStateFields = useCallback((field, payload) => {
        dispatch({
            type: stateTypes.UPDATE_STATE_FIELD,
            field,
            payload,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const enableClearAllButton = () => {
        // const enableClearAllButton = appliedFilters => {
        //Enable Clear All button only if a filter is already applied
        // let enableClearAllBtn = false
        // appliedFilters &&
        //     appliedFilters.length &&
        //     appliedFilters.forEach(({ key, display_value, isNotFilterSideMenu = false }) => {
        //         if (
        //             key !== 'name' &&
        //             !isNotFilterSideMenu &&
        //             display_value.length > 0 &&
        //             !enableClearAllBtn
        //         ) {
        //             enableClearAllBtn = true
        //             dispatch({
        //                 type: stateTypes.UPDATE_STATE_FIELD,
        //                 field: 'enableClearAll',
        //                 payload: true,
        //             })
        //         }
        //     })
    }

    const replaceListField = useCallback((value, name) => {
        dispatch({
            type: stateTypes.REPLACE_LIST_FIELD,
            field: name,
            payload: [value],
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const clearInputField = useCallback(name => {
        dispatch({
            type: stateTypes.CLEAR_INPUT_FIELD,
            field: name,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return {
        applyBtnRef,
        audienceChangeHandler,
        checkboxHandler,
        clearAllHandler,
        closeSideMenuHandler,
        dateChangeHandler,
        dispatch,
        filterApplyHandler,
        inputChangeHandler,
        removeFilterList,
        state,
        stateTypes,
        onSelectInputChange,
        updateFilterList,
        updateStateFields,
        filterMenuRef,
        replaceListField,
        clearInputField,
    }
}
